import { useTranslation } from 'react-i18next'

import { ExternalLink } from 'lucide-react'

import { track } from '@/api/tracking'
import { SubpageFoodCostHeader } from '@/components/food-cost/SubpageFoodCostHeader'
import { PRESS_MENTIONS } from '@/features/press/pressMentions'
import { LocaleSeoLinks } from '@/navigation/LocaleSeoLinks'
import { ROUTES, SITE_ORIGIN } from '@/navigation/constants'
import { absoluteLocalizedUrl } from '@/navigation/locale'
import { useLandingLocale } from '@/navigation/useLandingLocale'
import { DEFAULT_SHARE_IMAGE } from '@/shared/lib/siteSeo'
import { Footer } from '@/shared/ui/Footer'
import { PageHelmet } from '@/shared/ui/PageHelmet'
import { SocialShareMeta } from '@/shared/ui/SocialShareMeta'
import '@/styles/food-cost-fonts.css'

export function PressPage() {
    const { t } = useTranslation()
    const locale = useLandingLocale()

    const title = t('pressPage.meta.title', 'Tipi en la prensa')
    const description = t(
        'pressPage.meta.description',
        'Menciones de Tipi en medios: HIP, Lanzadera, escandallos por voz y control de costes en hostelería.',
    )
    const canonicalUrl = absoluteLocalizedUrl(ROUTES.press, locale)

    const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: title,
        description,
        url: canonicalUrl,
        isPartOf: {
            '@type': 'WebSite',
            name: 'Tipi',
            url: SITE_ORIGIN,
        },
        mainEntity: {
            '@type': 'ItemList',
            itemListElement: PRESS_MENTIONS.map((mention, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                url: mention.url,
                name: mention.title,
                item: {
                    '@type': 'NewsArticle',
                    headline: mention.title,
                    datePublished: mention.date,
                    url: mention.url,
                    publisher: {
                        '@type': 'Organization',
                        name: mention.outlet,
                    },
                    about: {
                        '@type': 'Organization',
                        name: 'Tipi',
                        url: SITE_ORIGIN,
                    },
                },
            })),
        },
    }

    return (
        <div className="flex min-h-screen flex-col bg-[#F5EEE4] font-['Archivo',system-ui,sans-serif] text-[#17130E] antialiased">
            <LocaleSeoLinks path={ROUTES.press} locale={locale} />
            <SocialShareMeta title={title} description={description} imageUrl={DEFAULT_SHARE_IMAGE} url={canonicalUrl} />
            <PageHelmet locale={locale}>
                <title>{title}</title>
                <meta name='description' content={description} />
                <link
                    rel='preload'
                    href='/fonts/Archivo/archivo-latin.woff2'
                    as='font'
                    type='font/woff2'
                    crossOrigin='anonymous'
                />
                <script type='application/ld+json'>{JSON.stringify(jsonLd)}</script>
            </PageHelmet>

            <SubpageFoodCostHeader />

            <main className='mx-auto w-full max-w-[720px] flex-1 px-[22px] pb-12 pt-24 md:px-10 md:pb-16 md:pt-28'>
                <div className='mb-8 flex flex-col gap-2.5'>
                    <div className='text-xs font-semibold uppercase tracking-[2px] text-[#F4502A]'>
                        {t('pressPage.eyebrow')}
                    </div>
                    <h1 className='m-0 text-[32px] font-medium leading-[1.15] tracking-[-1px] text-[#17130E] md:text-[46px]'>
                        {t('pressPage.title')}
                    </h1>
                    <p className='m-0 max-w-[52ch] text-base leading-relaxed text-[#5C5548]'>{t('pressPage.intro')}</p>
                </div>

                <ul className='m-0 list-none p-0'>
                    {PRESS_MENTIONS.map(mention => (
                        <li key={mention.id} className='border-t border-[#D8CFC0]'>
                            <a
                                href={mention.url}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='group flex flex-col gap-2 py-5 text-[#17130E] no-underline transition-colors hover:text-[#F4502A]'
                                onClick={() =>
                                    track('Press Mention Click', {
                                        landing_page: 'press',
                                        mention_id: mention.id,
                                        outlet: mention.outlet,
                                        url: mention.url,
                                    })
                                }
                            >
                                <div className='flex flex-wrap items-baseline gap-x-3 gap-y-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8A7F6F]'>
                                    <span>{mention.outlet}</span>
                                    <time dateTime={mention.date}>{mention.dateLabel}</time>
                                </div>
                                <div className='flex items-start justify-between gap-3'>
                                    <h2 className='m-0 text-base font-semibold leading-snug md:text-[17px]'>
                                        {mention.title}
                                    </h2>
                                    <ExternalLink
                                        className='mt-0.5 h-4 w-4 shrink-0 text-[#8A7F6F] transition-colors group-hover:text-[#F4502A]'
                                        strokeWidth={2.2}
                                        aria-hidden
                                    />
                                </div>
                                <p className='m-0 max-w-[52ch] text-[15px] leading-[1.65] text-[#5C5548]'>
                                    {mention.excerpt}
                                </p>
                                <span className='text-sm font-semibold text-[#F4502A]'>
                                    {t('pressPage.readArticle')}
                                </span>
                            </a>
                        </li>
                    ))}
                </ul>
            </main>

            <Footer landingPage='press' className='border-[#F5EEE4]/10 bg-[#17130E]' />
        </div>
    )
}
